import { Component, type ErrorInfo, type ReactNode } from "react";

const RELOAD_FLAG = "av-error-boundary-reloaded";

/** True for the "new code shipped while your tab was open" case: a lazy
 * route's chunk file no longer exists under its old hashed filename
 * because a newer deploy replaced it. Every bundler/browser phrases this
 * slightly differently, so match broadly rather than one exact string. */
function isStaleChunkError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error);
  return /dynamically imported module|error loading dynamically imported module|importing a module script failed|failed to fetch dynamically imported module/i.test(
    msg,
  );
}

interface State {
  error: Error | null;
}

/**
 * Last-resort catch-all so an uncaught render error (most commonly a
 * lazy-loaded route chunk that no longer exists after a new deploy) shows
 * a recoverable screen instead of leaving the whole app blank. A stale
 * chunk is self-healing -- one hard reload picks up the current build --
 * so that case reloads automatically rather than showing the user
 * anything. Guarded by a sessionStorage flag so a *genuinely* broken
 * chunk can't reload-loop forever.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isStaleChunkError(error) && !sessionStorage.getItem(RELOAD_FLAG)) {
      sessionStorage.setItem(RELOAD_FLAG, "1");
      window.location.reload();
      return;
    }
    console.error("Unhandled render error:", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      if (isStaleChunkError(this.state.error)) {
        // Reload is already in flight (or looped once already and failed
        // for a different reason) -- avoid flashing raw error text.
        return null;
      }
      return (
        <div style={{ maxWidth: 480, margin: "15vh auto", padding: 24, textAlign: "center" }}>
          <h1 style={{ fontSize: 20, marginBottom: 8 }}>Something went wrong</h1>
          <p className="muted" style={{ marginBottom: 20 }}>
            {this.state.error.message || "The page hit an unexpected error."}
          </p>
          <button type="button" className="btn primary" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
